import React from 'react';
import { connect } from 'react-redux';
import { Button, Input } from 'reactstrap';
import { fobiCore } from '../../FobiCore';
import { addTeilnahmeSanitizeEFNs, addTeilnahmeRemoveUngueltigeEFNs, addTeilnahmeSetEFN,
	addTeilnehmer } from '../../store/punkteMeldung/actions';
import { parseListe, istUebernehmbar, bleibtStehen, uebernehmbareEFNs, istEinzelmodus,
	istGueltigeEFNEingabe, istEFNImWerden, entferneFremdinhaltJeZeile, restText } from '../../store/punkteMeldung/listenEingabe';
import { isEFNinTeilnahmeMeldungen } from '../../store/punkteMeldung/commonUtils';

/**
 * Das Eingabefeld fuer EFNs - eine Textarea, damit eine aus Excel oder Word eingefuegte Liste
 * ihre Zeilenumbrueche behaelt. Erst dadurch kann jede EFN ihren eigenen Namen tragen; ein
 * <input type="text"> schluckt sie, und dann gilt die Liste als eine einzige Zeile.
 *
 * `Enter` uebernimmt wie bisher - daran haengt der Rhythmus am Einlass und der Handscanner, der
 * die EFN tippt und mit CR abschliesst. Eine neue Zeile macht `Shift+Enter` (die verbreitete
 * Konvention) oder `Alt+Enter` (was Excel in der Zelle tut).
 */

const MAX_ZEILEN_SICHTBAR = 8;

/**
 * Leeren fragt nicht nach, sondern laesst sich zuruecknehmen. Eine Rueckfrage kostet bei jedem
 * Mal einen Klick, auch bei den neunundneunzig gewollten; ein Angebot zum Wiederherstellen kostet
 * nur im Fehlerfall etwas. Die Textarea selbst kann es nicht: nach einem programmatischen
 * Leeren ist ihr eigenes Undo (Cmd+Z) leer.
 *
 * Der gesicherte Text bleibt bewusst in der Komponente und geht nie in den Store - sonst stuenden
 * die eben geloeschten Klarnamen ueber `saveState` wieder im localStorage, und "leeren" waere
 * eine Falschaussage.
 */
const WIEDERHERSTELLEN_MS = 9000;
const AUSBLENDEN_MS = 600;

/** Kein Zeilenumbruch aus Versehen: nur Shift oder Alt machen einen, Enter uebernimmt. */
const istUebernahmeTaste = (e) =>
	e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey
	// Waehrend einer Komposition (asiatische Tastaturen, Android) ist Enter das Bestaetigen der
	// Eingabemethode und nicht unseres. Der React-SyntheticEvent fuehrt isComposing nicht selbst.
	&& !(e.nativeEvent && e.nativeEvent.isComposing) && e.keyCode !== 229;

const WAS_PASSIERT = {
	verdaechtig: 'bleibt stehen: sieht aus wie eine EFN',
	exponential: 'bleibt stehen: Excel-Exponentialschreibweise',
	mehrereEFNsMitBeifang: 'bleibt stehen: mehrere EFNs mit Beifang'
};

const wasPassiert = (zeile, schonErfasst, schonGemeldet, einzeln) => {
	const blockierend = Object.keys(WAS_PASSIERT).find(hinweis => zeile.hinweise.indexOf(hinweis) >= 0);
	if(blockierend) return { text: WAS_PASSIERT[blockierend], warnend: true };
	if(bleibtStehen(zeile)){
		if(zeile.status === 'ohneEFN') return { text: 'bleibt stehen: keine EFN in dieser Zeile', warnend: true };
		// Sonst ist die Zeile `ungueltig`, und das hat zwei sehr verschiedene Ursachen. Sie
		// pauschal als "mehrere EFNs" zu melden war schlicht falsch: meistens steht dort genau
		// eine EFN und daneben mehr Text, als ein Name sein kann.
		if(zeile.efns.length > 1) return { text: 'bleibt stehen: mehrere EFNs mit Beifang', warnend: true };
		return { text: 'bleibt stehen: neben der EFN steht mehr, als ein Name sein kann', warnend: true };
	}
	if(schonErfasst === 'alle') return { text: einzeln ? 'vorhandene Teilnahme wird aktualisiert' : 'bereits erfasst – Punkte bleiben', warnend: false };
	if(schonErfasst === 'teils') return { text: 'teils neu, teils bereits erfasst – Punkte bleiben', warnend: false };
	if(schonGemeldet) return { text: 'schon gemeldet – als Korrektur', warnend: false };
	return { text: 'wird übernommen', warnend: false };
};

const Vorschau = (props) => {
	const { zeilen, einzeln, istErfasst, istGemeldet } = props;
	return (
		<div className="listen-vorschau mt-2">
			<table className="table table-sm table-borderless align-middle mb-0">
				<thead>
					<tr className="small text-body-secondary">
						<th className="listen-vorschau-nr">Nr.</th>
						<th>EFN</th>
						<th>Name / Notiz</th>
						<th>Was passiert</th>
					</tr>
				</thead>
				<tbody>
					{zeilen.map(zeile => {
						// Eine Zeile kann mehrere EFNs tragen - dann ist zeile.efn undefined, und eine
						// Pruefung darauf haette immer "wird uebernommen" gesagt.
						const erfasste = zeile.efns.filter(istErfasst).length;
						const lage = wasPassiert(zeile,
							erfasste === 0 ? false : (erfasste === zeile.efns.length ? 'alle' : 'teils'),
							zeile.efns.some(istGemeldet), einzeln);
						return (
							<tr key={zeile.nr} className={lage.warnend ? 'text-warning-emphasis' : ''}>
								<td className="listen-vorschau-nr">{zeile.nr}</td>
								<td className="listen-vorschau-efn">{zeile.efns.length ? zeile.efns.join(' ') : '–'}</td>
								{/* Bleibt eine Zeile stehen, zeigt die Vorschau ihren Wortlaut - sonst
								    stuende dort nichts, und man fände die Zeile im Feld nicht wieder. */}
								<td className={istUebernehmbar(zeile) ? '' : 'text-body-secondary'}>
									{istUebernehmbar(zeile) ? zeile.text : zeile.roh}
								</td>
								<td className="small">
									{lage.text}
									{zeile.hinweise.indexOf('weitereSpalten') >= 0 &&
										<div className="text-body-secondary">weitere Spalten erkannt</div>}
									{zeile.hinweise.indexOf('mehrfach') >= 0 &&
										<div className="text-body-secondary">EFN mehrfach in der Liste</div>}
									{/* Wie beim zweiten Scan desselben Ausweises: die Teilnahme entsteht,
									    der zweite Name wird als Widerspruch gemerkt und in der Zeile
									    mit ergänzen / ersetzen / verwerfen aufgelöst. */}
									{zeile.hinweise.indexOf('widerspruch') >= 0 &&
										<div className="text-warning-emphasis">verschiedene Namen – Widerspruch wird gemerkt</div>}
									{/* Mehrere Spalten mit Buchstaben: welche der Name ist, ist nicht zu
									    entscheiden. Lieber ohne Namen übernehmen als mit dem falschen. */}
									{zeile.hinweise.indexOf('nameUneindeutig') >= 0 &&
										<div className="text-body-secondary">Name nicht eindeutig – EFN ohne Namen</div>}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

const ListenEingabe = (props) => {
	const { eingabe, zeilen, gueltig, einzeln, listenmodus, imWerden, anzahlUebernehmbar, anzahlRest,
		anzahlNeu, anzahlSchonErfasst, anzahlWeitereSpalten, istBereitsGemeldet, beschriftungAusEingabe,
		invalidEFNs, countInvalidEFNs, zeigeBereinigen, zeigeUngueltigeEntfernen,
		istErfasst, istGemeldet, hinweisEinzeln, changeEFN, uebernehmen, sanitizeEFNs,
		removeUngueltigeEFNs } = props;

	const [wiederherstellung, setWiederherstellung] = React.useState(null);
	// Ein erfolgloser Uebernahmeversuch: ab dann spricht die Rueckmeldung wieder. Zurueckgenommen
	// bei der naechsten Eingabe - wer weitertippt, redigiert, und dann schweigt es erneut.
	const [versucht, setVersucht] = React.useState(false);
	const uhren = React.useRef([]);
	const stoppeUhren = () => { uhren.current.forEach(clearTimeout); uhren.current = []; };
	// Beim Verlassen der Veranstaltung darf kein Timer mehr in eine abgeraeumte Komponente
	// schreiben - und der gesicherte Text ist damit auch weg.
	React.useEffect(() => stoppeUhren, []);

	// Tippt oder fuegt jemand nach dem Leeren etwas ein, ist das Angebot hinfaellig: ein
	// Wiederherstellen wuerde die neue Eingabe ueberschreiben.
	React.useEffect(() => {
		if(eingabe.length > 0){ stoppeUhren(); setWiederherstellung(null); }
	}, [eingabe]);

	const leeren = () => {
		const gesichert = eingabe;
		changeEFN("");
		stoppeUhren();
		setWiederherstellung({ text: gesichert, verblasst: false });
		uhren.current = [
			setTimeout(() => setWiederherstellung(w => w && { ...w, verblasst: true }), WIEDERHERSTELLEN_MS - AUSBLENDEN_MS),
			setTimeout(() => setWiederherstellung(null), WIEDERHERSTELLEN_MS)
		];
	};

	const wiederherstellen = () => {
		stoppeUhren();
		changeEFN(wiederherstellung.text);
		setWiederherstellung(null);
	};

	// Drei Zustaende statt gueltig/ungueltig: leuchtete das Feld gruen, waehrend eine Zeile
	// liegenbleibt, waere die Farbe eine Falschaussage.
	const teilweise = gueltig && (anzahlRest > 0 || anzahlWeitereSpalten > 0);
	// Waehrend eine EFN noch getippt wird, schweigt alles: jeder Zwischenstand einer 15-stelligen
	// Zahl ist ungueltig, und "listenmodus" hiess bis dahin schlicht "keine gueltige Einzel-EFN" -
	// eine halb getippte Nummer landete damit im selben Topf wie eine Liste aus Excel.
	// Enter bricht das Schweigen: geprueft wird beim Absenden, nicht bei jedem Anschlag.
	const stumm = imWerden && !versucht;
	const listenmodusSichtbar = listenmodus && !stumm;
	// Ohne fuehrendes form-control: reactstrap setzt es fuer die Textarea selbst.
	let klassen = '';
	if(eingabe.length > 0 && !gueltig && !stumm) klassen = 'is-invalid';
	else if(teilweise) klassen = 'is-teilweise';

	// "Liste" heisst mehr als eine Zeile. Eine einzelne unbrauchbare Zeile ist keine Liste -
	// dort stand vorher "Liste übernehmen" an einem toten Knopf.
	const listeImKnopf = zeilen.length > 1;
	const knopfText = !listeImKnopf ? 'Hinzufügen'
		: teilweise ? `Liste übernehmen (${anzahlUebernehmbar})` : 'Liste übernehmen';

	return (
		<>
			<div className="input-group listen-eingabe-gruppe">
				<Input valid={gueltig && !teilweise} type="textarea"
					className={klassen} required={true} autoFocus={true}
					rows={Math.max(1, Math.min(MAX_ZEILEN_SICHTBAR, zeilen.length || 1))}
					style={{ resize: 'none' }}
					value={eingabe}
					// Im Listenmodus nicht alles markieren: ein Klick zum Korrigieren wuerde sonst
					// die ganze Restliste auswaehlen, und der naechste Tastendruck loescht sie.
					onFocus={(e) => { if(!listenmodusSichtbar) e.target.select(); }}
					onKeyDown={(e) => { if(istUebernahmeTaste(e)){ e.preventDefault();
						if(!gueltig) setVersucht(true);
						uebernehmen(); } }}
					onChange={(e) => { setVersucht(false); changeEFN(e.target.value); }}
					title="EFN(s) – eine Liste aus Excel oder Word kann eingefügt werden. Enter übernimmt, Shift+Enter macht eine neue Zeile."
					placeholder="EFN(s) – Liste einfügen möglich" />
				&nbsp;<button type="button" disabled={!gueltig} onClick={uebernehmen}
					className="btn btn-secondary">{knopfText}</button>
				{istBereitsGemeldet &&
					<div className="valid-feedback text-primary">Für diese EFN gibt es bereits Punktemeldung(en). Ist es eine Korrektur?</div>
				}
				{/* Vor der Übernahme zeigen, was als Beschriftung stehen bleibt - der Anwender
				    sieht das Ergebnis des Aufräumens, bevor es in der Liste landet. */}
				{beschriftungAusEingabe &&
					<div className="valid-feedback d-block">
						Name / Notiz: „{beschriftungAusEingabe.text}“
					</div>
				}
				{/* Ohne Vorschau (eine Zeile) gaebe es sonst gar keine Rueckmeldung dazu, dass der
				    Name verworfen wurde - er verschwaende einfach. */}
				{hinweisEinzeln &&
					<div className="valid-feedback d-block text-body-secondary">{hinweisEinzeln}</div>
				}
				{/* Der Waechter steht um den ganzen Block, nicht nur am Listenmodus: er hat vier
				    Oeffner, und die anderen drei zoegen ihn sonst weiter auf - mit "Das ist keine
				    EFN", *bereinigen* (schon ab fuenf Ziffern) und *ungueltige entfernen*. */}
				{!stumm && (invalidEFNs || zeigeBereinigen || zeigeUngueltigeEntfernen || listenmodusSichtbar) &&
					<div className={invalidEFNs?"invalid-feedback text-danger d-block":"valid-feedback d-block"}>
					{ invalidEFNs &&
						<span>{ countInvalidEFNs === 1?"Das ist keine EFN: ":"Das sind keine EFNs: " }{ invalidEFNs }</span>
					}
					{/* Zweistufig, damit das Verwerfen einer fraglichen Nummer eine eigene
					    Entscheidung ist und nicht als Nebenwirkung des Aufraeumens passiert. */}
					{ zeigeBereinigen &&
						<Button title="Namen und Notizen entfernen – nur die Nummern behalten" className="mx-2" onClick={sanitizeEFNs} color="info" outline size="sm">bereinigen</Button>
					}
						{ zeigeUngueltigeEntfernen &&
						<Button title="Diese Nummern verwerfen – die Teilnehmer werden dann nicht gemeldet" className="mx-2" onClick={removeUngueltigeEFNs} color="warning" outline size="sm">ungültige entfernen</Button>
					}
					{/* Erst noetig, seit im Listenmodus nicht mehr alles beim Hineinklicken markiert
					    wird: vierzig Zeilen von Hand zu loeschen ist keine Bedienung. Ohne Rueckfrage -
					    dafuer steht danach kurz das Wiederherstellen bereit. */}
					{ listenmodusSichtbar &&
						<Button title="Das Eingabefeld leeren – lässt sich gleich danach zurücknehmen" className="mx-2" onClick={leeren} color="secondary" outline size="sm">leeren</Button>
					}
					</div>
				}
				{/* Eigene Zeile statt im Feedback-Block: der verschwindet mit dem Listenmodus in
				    demselben Augenblick, in dem das Feld leer wird. */}
				{ wiederherstellung &&
					<div role="status" aria-live="polite"
						className={"d-block w-100 small mt-1 listen-undo" + (wiederherstellung.verblasst ? " listen-undo-aus" : "")}>
						<span className="text-body-secondary">Eingabe geleert.</span>
						<Button className="mx-2" onClick={wiederherstellen} color="secondary" outline size="sm">wiederherstellen</Button>
					</div>
				}
			</div>

			{ listenmodusSichtbar &&
				<>
					<div className="small mt-2">
						<b>{anzahlNeu}</b> neue {anzahlNeu === 1 ? "Teilnehmerin/Teilnehmer" : "Teilnehmende"}
						{anzahlSchonErfasst > 0 && <>, {anzahlSchonErfasst} bereits erfasst</>}
						{anzahlRest > 0 && <>, <span className="text-warning-emphasis">{anzahlRest} {anzahlRest === 1 ? "Zeile bleibt" : "Zeilen bleiben"} stehen</span></>}
					</div>
					{ anzahlWeitereSpalten > 0 &&
						<div className="small text-warning-emphasis">
							{anzahlWeitereSpalten} {anzahlWeitereSpalten === 1 ? "Zeile enthält" : "Zeilen enthalten"} weitere
							Spalten – bitte die Spalte <i>Name / Notiz</i> prüfen.
						</div>
					}
					<Vorschau zeilen={zeilen} einzeln={einzeln} istErfasst={istErfasst} istGemeldet={istGemeldet} />
				</>
			}
		</>
	);
};

const mapStateToProps = (state, ownProps) => {
	const vnr = ownProps.veranstaltung.vnr;
	const eingabe = state.punkteMeldung.addTeilnahme[vnr].efn;
	const { zeilen } = parseListe(eingabe);
	const einzeln = istEinzelmodus(eingabe);
	const erfassteOfVNR = state.punkteMeldung.erfassteTeilnahmen[vnr] || {};
	const uebernehmbare = uebernehmbareEFNs(zeilen);
	// Beides sind Aussagen ueber das, was *liegen bleibt*. Auf die ganze Eingabe angewandt
	// meldete "802760600165921 Praxis 2" die 2 als "keine EFN" und bot bereinigen an, obwohl
	// die Zeile gueltig ist und "Praxis 2" eine gewollte Notiz - genau das, was die App zusagt.
	const rest = restText(zeilen);
	const invalideEFNs = fobiCore.extractInvalidEFNsToArray(rest);
	// Erst aufraeumen, dann verwerfen - immer nur ein Knopf sichtbar.
	const zeigeBereinigen = Boolean(rest) && rest.trim() !== entferneFremdinhaltJeZeile(rest);
	// Dieselbe Entscheidung wie beim Import: was nicht Einzelmodus ist, ist Listenmodus. An der
	// Zahl der *verschiedenen* EFNs festzumachen ging daneben - "<EFN> <dieselbe EFN>" zeigte
	// "Hinzufuegen" und keine Vorschau, waehrend der Parser die Zeile korrekt als Liste behandelt.
	const listenmodus = !einzeln && zeilen.length > 0;
	return {
		eingabe,
		zeilen,
		einzeln,
		listenmodus,
		gueltig: istGueltigeEFNEingabe(eingabe),
		imWerden: istEFNImWerden(eingabe),
		anzahlUebernehmbar: zeilen.filter(istUebernehmbar).length,
		anzahlRest: zeilen.filter(bleibtStehen).length,
		anzahlNeu: uebernehmbare.filter(efn => !erfassteOfVNR[efn]).length,
		anzahlSchonErfasst: uebernehmbare.filter(efn => erfassteOfVNR[efn]).length,
		anzahlWeitereSpalten: zeilen.filter(zeile => zeile.hinweise.indexOf('weitereSpalten') >= 0).length,
		// Bei einer Liste zerlegte die Aufzaehlung jede Jahreszahl in Fragmente
		// ("5; 30; 08; 2026") - dort sagt die Vorschau je Zeile, was passiert.
		invalidEFNs: listenmodus ? "" : invalideEFNs.join("; "),
		countInvalidEFNs: listenmodus ? 0 : invalideEFNs.length,
		zeigeBereinigen,
		zeigeUngueltigeEntfernen: !zeigeBereinigen && fobiCore.extractVerdaechtigeEFNsToArray(rest).length > 0,
		// Beides sind Aussagen ueber *die eine* EFN im Feld - im Listenmodus steht das je Zeile
		// in der Vorschau.
		beschriftungAusEingabe: einzeln && zeilen[0].text ? zeilen[0] : null,
		hinweisEinzeln: !listenmodus && zeilen.length === 1 && zeilen[0].hinweise.indexOf('nameUneindeutig') >= 0
			? "Mehrere Spalten kommen als Name in Frage – die EFN wird ohne Namen übernommen."
			: null,
		istBereitsGemeldet: einzeln && isEFNinTeilnahmeMeldungen(zeilen[0].efn, vnr, state),
		istErfasst: (efn) => Boolean(efn && erfassteOfVNR[efn]),
		istGemeldet: (efn) => Boolean(efn && isEFNinTeilnahmeMeldungen(efn, vnr, state))
	};
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	changeEFN: (efn) => dispatch(addTeilnahmeSetEFN(ownProps.veranstaltung.vnr, efn)),
	// Der Thunk liest den Text selbst aus dem Store - die Komponente reicht nichts durch, was
	// zwischen zwei schnellen Enter-Ereignissen veralten koennte.
	uebernehmen: () => dispatch(addTeilnehmer(ownProps.veranstaltung.vnr)),
	sanitizeEFNs: () => dispatch(addTeilnahmeSanitizeEFNs(ownProps.veranstaltung.vnr)),
	removeUngueltigeEFNs: () => dispatch(addTeilnahmeRemoveUngueltigeEFNs(ownProps.veranstaltung.vnr))
});

export default connect(mapStateToProps, mapDispatchToProps)(ListenEingabe);
